import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import ContentBox from "../../../components/ContentBox";
import CoversDragAndDrop from "../../../components/Covers/DragAndDropRHF";
import CoversTransformRHF from "../../../components/Covers/TransformRHF";
import LocaleSelect from "../../../components/LocaleSelect";
import FormController from "../../../components/ReactHookForm/FormController";
import InvalidLocalesRHF from "../../../components/ReactHookForm/InvalidLocalesRHF";
import ObserverRHF from "../../../components/ReactHookForm/ObserverRHF";
import { RESOURCES_COVERIZABLE_OBJECT_TYPES } from "../../../constants/resources";
import {
  configurationCoversGetSizes,
  configurationGet,
} from "../../../helpers/configuration";
import { useConfiguration } from "../../../hooks/useConfiguration";
import useLocale from "../../../hooks/useLocale";

/**
 * @typedef {Object} Props
 * @property {boolean} isOpen
 * @property {boolean} isLoading
 * @property {() => void} onClose
 * @property {string} coverName
 * @property {string} transformationsName
 * @property {string} objectType
 * @property {boolean} [withLegend]
 *
 * @param {Props} props
 */
export default function CoverModal({
  isOpen,
  isLoading,
  onClose,
  coverName,
  transformationsName,
  objectType,
  withLegend = false,
}) {
  const { defaultLocale } = useLocale();

  const [activeLocale, setActiveLocale] = useState(defaultLocale);

  const environmentId = useWatch({ name: "data.environment_id" });

  const intl = useIntl();

  const { configuration } = useConfiguration();

  const { formats, constraints } = configurationGet({
    configuration,
    key: (c) => c?.covers?.models,
    environmentId: environmentId,
  })[RESOURCES_COVERIZABLE_OBJECT_TYPES[objectType].model];

  const { minWidth, minHeight } = configurationCoversGetSizes({ formats });

  /**
   * @param {import("react").ChangeEvent<HTMLSelectElement>} event
   */
  function handleChangeLocale(event) {
    setActiveLocale(event.target.value);
  }

  return (
    <Modal
      isOpen={isOpen}
      size="xl"
      onClose={onClose}
      scrollBehavior="inside"
      isCentered>
      <ModalOverlay />

      <ModalContent>
        <ModalCloseButton />

        <ModalHeader>
          {intl.formatMessage({
            defaultMessage: "Votre image",
          })}
        </ModalHeader>

        <ModalBody>
          <VStack w="full" alignItems="baseline" spacing="1rem">
            <CoversDragAndDrop
              constraints={constraints}
              coverName={coverName}
              transformationsName={transformationsName}
              minWidth={minWidth}
              minHeight={minHeight}
            />

            <ObserverRHF
              names={[coverName]}
              render={({ values: [cover] }) => (
                <>
                  {cover && (
                    <>
                      <ContentBox
                        p="1rem"
                        w="full"
                        title={
                          <Text
                            fontWeight="bold"
                            fontSize="1.125rem"
                            mb="1.5rem">
                            {intl.formatMessage({
                              defaultMessage: "Format de l’image",
                            })}
                          </Text>
                        }>
                        <CoversTransformRHF
                          coverName={coverName}
                          transformationsName={transformationsName}
                          variant="expanded"
                          formats={formats}
                        />
                      </ContentBox>

                      {withLegend && (
                        <ContentBox
                          p="1rem"
                          w="full"
                          title={
                            <Text
                              fontWeight="bold"
                              fontSize="1.125rem"
                              mb="1.5rem">
                              {intl.formatMessage({
                                defaultMessage: "Métadonnées",
                              })}
                            </Text>
                          }>
                          <Stack spacing="1rem">
                            <HStack justify="flex-end">
                              <InvalidLocalesRHF
                                render={({ invalidLocales }) => (
                                  <LocaleSelect
                                    width="auto"
                                    value={activeLocale}
                                    onChange={handleChangeLocale}
                                    borderColor={
                                      !invalidLocales.includes(
                                        activeLocale ?? "",
                                      ) && invalidLocales.length > 0
                                        ? "red.500"
                                        : undefined
                                    }
                                  />
                                )}
                              />
                            </HStack>

                            <FormController
                              name={`data.${coverName}.caption.${activeLocale}`}
                              key={`data.${coverName}.caption.${activeLocale}`}
                              label={intl.formatMessage({
                                defaultMessage: "Légende",
                              })}
                              defaultValue=""
                              type="textarea"
                            />
                          </Stack>
                        </ContentBox>
                      )}
                    </>
                  )}
                </>
              )}
            />
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            isDisabled={isLoading}
            onClick={onClose}
            colorScheme="brandPrimary">
            {intl.formatMessage({
              defaultMessage: "Confirmer",
            })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
